import React, { useEffect, useState } from 'react';
import { getAdminStats } from '../../utils/api/adminStats';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import Line from './Line';

const PlatformStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAdminStats()
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setStats(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return <div className="py-12 text-gray-400 text-sm">Loading stats...</div>;
  }

  if (error) {
    return <div className="py-12 text-red-600 text-sm">{error}</div>;
  }

  const workersReached = stats.workers_reached;
  const sitesVisited = stats.sites_visited;
  const highPriorityNotes = stats.high_priority_notes;
  const statesVisited = stats.states_visited;
  const { visits, notes } = stats;

  // Parse "Manager: 4 | Planner: 2 | User: 4" into individual entries
  const userRoles = stats.users.split(' | ').map((entry) => {
    const [role, count] = entry.split(': ');
    return { role, count: parseInt(count, 10) };
  });

  return (
    <div className="max-w-5xl px-4 py-6">
      <div className="flex items-center justify-between mb-8">
        <PagesHeader name="reports" label="Platform Stats" />
        <a
          href="/api/v1/admin/stats/export"
          download="Admin Report.csv"
          className="flex justify-center items-center text-lg font-normal px-3 py-1.5 h-11.5 min-w-29 rounded-md bg-vamos-black text-vamos-white hover:bg-vamos-green1 whitespace-nowrap"
        >
          Export
        </a>
      </div>

      <div className="space-y-6 text-gray-800">

        <p className="text-sm text-gray-500">Report date: {stats.report_date}</p>

        <div>
          <p className="text-base leading-6">
            This report contains data about the {stats.organizations.length}{' '}
            {stats.organizations.length === 1 ? 'organization' : 'organizations'} that currently
            have an active account with Vamos. Trial accounts are not included.
          </p>
          <p className="text-base font-semibold mb-2 mt-6">Organizations included in this report</p>
          <ul className="text-base mt-1 space-y-0.5 list-disc list-inside">
            {stats.organizations.map((org) => (
              <li key={org}>{org}</li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-base font-semibold mb-2">Users</p>
          {userRoles.map(({ role, count }) => (
            <Line key={role} label={role} value={count} />
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-base font-semibold mb-2">Sites</p>
            <Line label="Total Sites" value={stats.total_sites} />
            <Line label="Total sites visited" value={sitesVisited.all_time} />
            <Line label="Total sites visited in the past 12 months" value={sitesVisited.past_12_months} />
            <Line label="Total sites visited since January" value={sitesVisited.ytd} />
          </div>

          <div>
            <p className="text-base font-semibold mb-2">Visits</p>
            <Line label="Total Visits" value={visits.all_time} />
            <Line label="Visits in the past 12 months" value={visits.past_12_months} />
            <Line label="Visits since January this year" value={visits.ytd} />
          </div>

          <div>
            <p className="text-base font-semibold mb-2">Workers reached</p>
            <Line label="Total workers reached" value={workersReached.all_time} />
            <Line label="Total workers reached in the past 12 months" value={workersReached.past_12_months} />
            <Line label="Total workers reached since January" value={workersReached.ytd} />
          </div>

          <div>
            <p className="text-base font-semibold mb-2">Notes</p>
            <Line label="Total Notes" value={notes.all_time} />
            <Line label="Notes in the past 12 months" value={notes.past_12_months} />
            <Line label="Notes since January" value={notes.ytd} />
            <Line label="High priority notes" value={highPriorityNotes.all_time} />
            <Line label="High priority notes in the past 12 months" value={highPriorityNotes.past_12_months} />
            <Line label="High priority notes since January" value={highPriorityNotes.ytd} />
          </div>

          <div>
            <p className="text-base font-semibold mb-2">States</p>
            <Line label="States visited" value={statesVisited.all_time} />
            <Line label="States visited in the past 12 months" value={statesVisited.past_12_months} />
            <Line label="States visited since January" value={statesVisited.ytd} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default PlatformStatsPage;
